import { useEffect, useRef } from 'react'
import isEqual from 'lodash-es/isEqual'

import { EMPTY_LIST } from '../../constants/common'
import { useInfiniteItemsLoader } from '../useInfiniteItemsLoader'

import { getApiParamsFromData } from './useInfiniteItemsListLoader.utils'

export function useInfiniteItemsListLoader({
  apiParams,
  checkIsLastPage,
  filters,
  getApiParamsInit = getApiParamsFromData,
  getApiParamsLoadNext = getApiParamsFromData,
  getApiParamsUpdate = getApiParamsFromData,
  getData,
  getDataItems,
  getMergedItems,
  getPermissionsData,
  getTotalCountData,
  loadPermission = true,
  observerOptions,
  preloading = false,
  setAdditionalData,
  thresholdEndIndex,
  toPreparedItems,
  withPagination,
  onChangeInitialized,
  onChangePermissions,
  onChangeTotalCount,
  onFilterItems,
}) {
  const {
    getThresholdItemProps,
    hasError,
    hasMoreRef,
    items,
    loadingInitial,
    loadingMore,
    loadInitial,
    loadMore,
    setItems,
    totalCount,
  } = useInfiniteItemsLoader({
    apiParams,
    checkIsLastPage,
    getApiParamsLoadNext,
    getData,
    getDataItems,
    getMergedItems,
    getPermissionsData,
    getTotalCountData,
    observerOptions,
    setAdditionalData,
    thresholdEndIndex,
    toPreparedItems,
    withPagination,
    onChangeInitialized,
    onChangePermissions,
    onChangeTotalCount,
  })

  const prevApiParams = useRef(null)
  const autoLoadItemsRef = useRef(null)

  useEffect(() => {
    if (
      !preloading &&
      !isEqual(apiParams, prevApiParams.current) &&
      loadPermission
    ) {
      prevApiParams.current = apiParams

      setItems(EMPTY_LIST)

      loadInitial(getApiParamsInit(items))
    }
  }, [apiParams, loadPermission, preloading])

  useEffect(() => {
    if (filters) {
      setItems(list => onFilterItems(filters, list))
    }
  }, [filters])

  useEffect(() => {
    const isSettled = !loadingInitial && !loadingMore
    const isEmpty = (items?.length ?? 0) === 0
    const advancedSinceLastAttempt = items !== autoLoadItemsRef.current

    if (
      isSettled &&
      isEmpty &&
      !hasError &&
      hasMoreRef.current &&
      advancedSinceLastAttempt
    ) {
      autoLoadItemsRef.current = items

      loadMore(getApiParamsLoadNext(items))
    }
  }, [hasError, items, loadingInitial, loadingMore])

  function updateListData() {
    onChangeInitialized?.(false)

    setItems(EMPTY_LIST)

    return loadInitial(getApiParamsUpdate())
  }

  function mutateItems(updater) {
    setItems(prev => updater(prev ?? EMPTY_LIST))
  }

  return {
    getThresholdItemProps,
    hasError,
    items,
    loadingInitial,
    loadingMore,
    mutateItems,
    totalCount,
    updateListData,
  }
}
